// Joins Deribit's real futures_snapshot mark/index prices onto a
// vol-surface-derived curve (build-carry-curve.ts), matching dated
// instruments by expiry date (parse-deribit-expiry.ts). Tenors with no
// matching listed future (e.g. a vol-surface synthetic-future tenor) are
// left with futuresMark/etc as null, unchanged.
import { CarryCurvePoint, PerpPoint } from './types';
import { FuturesSnapshotInstrument } from '../../providers/exotics-tokens/use-futures-snapshot';
import { parseDeribitExpiry } from './parse-deribit-expiry';

// yearFrac is null for the perpetual (no expiry, nothing to annualize
// against) — futuresCarry comes back null in that case, same convention as
// build-carry-curve.ts's own null-guards. futuresForwardFactor needs no
// yearFrac at all (it's un-annualized), so it's still real for the perp.
const readInstrument = (
  match: FuturesSnapshotInstrument,
  yearFrac: number | null,
): {
  futuresMark: number | null;
  futuresIndex: number | null;
  futuresFwdPoints: number | null;
  futuresForwardFactor: number | null;
  futuresCarry: number | null;
} => {
  const futuresMark = typeof match.mark_price === 'number' ? match.mark_price : null;
  const futuresIndex = typeof match.index_price === 'number' ? match.index_price : null;
  const hasBoth =
    futuresMark !== null && futuresMark > 0 && futuresIndex !== null && futuresIndex > 0;
  const futuresFwdPoints = hasBoth ? futuresMark - futuresIndex : null;
  const futuresForwardFactor = hasBoth ? futuresMark / futuresIndex : null;
  const futuresCarry = hasBoth && yearFrac ? Math.log(futuresMark / futuresIndex) / yearFrac : null;
  return { futuresMark, futuresIndex, futuresFwdPoints, futuresForwardFactor, futuresCarry };
};

export function attachFuturesSnapshot(
  curve: CarryCurvePoint[],
  futures: FuturesSnapshotInstrument[] | null,
): CarryCurvePoint[] {
  if (!futures || futures.length === 0) return curve;

  const byTenor = new Map<string, FuturesSnapshotInstrument>();
  for (const f of futures) {
    if (f.instrument_name.endsWith('PERPETUAL')) continue;
    const parsed = parseDeribitExpiry(f.instrument_name);
    if (parsed) byTenor.set(parsed.tenor, f);
  }

  return curve.map((point) => {
    const match = byTenor.get(point.tenor);
    if (!match) return point;
    const { futuresMark, futuresFwdPoints, futuresForwardFactor, futuresCarry } = readInstrument(
      match,
      point.yearFrac,
    );
    // Basis = Forward Carry (options) - Futures Carry — only defined when
    // this tenor has a real matching listed future (futuresCarry non-null);
    // some options tenors (e.g. a vol-surface synthetic tenor) have no
    // matching futures tenor at all, so Basis stays null for those.
    const basis =
      futuresCarry !== null && point.forwardCarry !== null
        ? point.forwardCarry - futuresCarry
        : null;
    return { ...point, futuresMark, futuresFwdPoints, futuresForwardFactor, futuresCarry, basis };
  });
}

// Deribit settles funding every 8 hours — 3/day * 365 days/year.
const FUNDING_PERIODS_PER_YEAR = 3 * 365;

// The PERPETUAL contract has no expiry to join against a vol-surface tenor
// (see types.ts's PerpPoint doc) — pulled out separately as the curve's
// anchor row.
export function extractPerpPoint(futures: FuturesSnapshotInstrument[] | null): PerpPoint | null {
  const perp = futures?.find((f) => f.instrument_name.endsWith('PERPETUAL'));
  if (!perp) return null;
  const { futuresMark, futuresIndex, futuresFwdPoints, futuresForwardFactor } = readInstrument(
    perp,
    null,
  );
  const fundingRateAnnualized =
    typeof perp.funding_8h === 'number' && Number.isFinite(perp.funding_8h)
      ? perp.funding_8h * FUNDING_PERIODS_PER_YEAR
      : null;
  return {
    futuresMark,
    futuresIndex,
    futuresFwdPoints,
    futuresForwardFactor,
    fundingRateAnnualized,
  };
}
